import type { Field } from '@chakra-ui/react';
import { Spinner } from '@chakra-ui/react';
import { AsyncStatus } from '@monetize/types/common';
import { forwardRef } from 'react';
import {
  AsyncStatusIndicator,
  MBox,
  MSwitch,
} from '../../../components/Monetize';
import { SettingsControl } from './SettingsControl';

interface SettingsToggleProps {
  /**
   * Becomes the switch input's DOM `id`, so it must not reuse the enclosing
   * `SettingsSection`'s `id`: the switch root is a `<label htmlFor>`, which
   * resolves to the first element with that id and stops toggling.
   */
  name: string;
  heading: string;
  subheading?: string;
  fetchLoading?: boolean;
  value: boolean;
  isDisabled?: boolean;
  comingSoon?: boolean;
  asyncStatus?: AsyncStatus;
  formControlProps?: Field.RootProps;
  onChange: (value: boolean) => void | Promise<unknown>;
}

// Forwards its ref to the switch input: react-hook-form `Controller` renders
// spread `field` (including `field.ref`) into this component.
export const SettingsToggle = forwardRef<HTMLInputElement, SettingsToggleProps>(
  (
    {
      name,
      heading,
      subheading,
      fetchLoading = false,
      value,
      formControlProps,
      isDisabled = false,
      comingSoon = false,
      asyncStatus = 'idle',
      onChange,
    },
    ref,
  ) => {
    const control = fetchLoading ? (
      <Spinner size="md" />
    ) : (
      <MBox position="relative">
        <MSwitch
          ref={ref}
          id={name}
          aria-describedby={`${name}-subheading`}
          isDisabled={isDisabled || asyncStatus === 'loading'}
          isChecked={value}
          onChange={(event) => onChange(event.target.checked)}
        />
        <AsyncStatusIndicator status={asyncStatus} />
      </MBox>
    );

    return (
      <SettingsControl
        name={name}
        heading={heading}
        subheading={subheading}
        fetchLoading={fetchLoading}
        isDisabled={isDisabled || asyncStatus === 'loading'}
        comingSoon={comingSoon}
        formControlProps={formControlProps}
        control={control}
      />
    );
  },
);

SettingsToggle.displayName = 'SettingsToggle';
